import {
  GetSheetAgentRunsByTenderIdDocument,
  GetSheetAgentRunsByTenderIdQuery,
  GetSheetAgentRunsByTenderIdQueryVariables,
  SheetAgentRunStatus,
} from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

/**
 * Custom hook to manage polling for sheet agent runs by tender ID.
 *
 * This hook uses `useQuery` to poll the sheet agent runs.
 * Polling continues automatically while any run has a status of Pending or ExtractingTasks.
 * Polling stops when all runs have a status other than Pending or ExtractingTasks.
 *
 * @param {string} tenderID - The tender ID to fetch sheet agent runs for
 * @returns {Object} An object containing:
 * - `data`: The data returned from the query.
 * - `loading`: Boolean indicating if the query is currently loading.
 * - `isPolling`: Boolean indicating if polling is active.
 * - `startPolling`: Function to start polling.
 * - `stopPolling`: Function to stop polling.
 * - `setIsPolling`: Function to set the polling state.
 * - `refetch`: Function to manually refetch the query.
 */
/**
 * Helper function to check if any runs have active statuses that require polling
 */
const hasActiveRuns = (runs: Array<{ status: SheetAgentRunStatus }>): boolean => {
  return runs.some(
    (run) =>
      run.status === SheetAgentRunStatus.Pending ||
      run.status === SheetAgentRunStatus.ExtractingTasks
  );
};

export const useGetSheetAgentRunsByTenderId = (tenderID: string): {
  data: GetSheetAgentRunsByTenderIdQuery | undefined;
  loading: boolean;
  isPolling: boolean;
  startPolling: (_pollInterval: number) => void;
  stopPolling: () => void;
  setIsPolling: Dispatch<SetStateAction<boolean>>;
  refetch: () => void;
} => {
  const [isPolling, setIsPolling] = useState(true);
  const tenderIdNumber = tenderID ? Number(tenderID) : 0;

  const { data, loading, refetch, startPolling, stopPolling } = useQuery<
    GetSheetAgentRunsByTenderIdQuery,
    GetSheetAgentRunsByTenderIdQueryVariables
  >(GetSheetAgentRunsByTenderIdDocument, {
    variables: {
      tenderId: tenderIdNumber,
    },
    skip: !tenderID || isNaN(tenderIdNumber),
    pollInterval: 0, // Start with no polling, we'll control it manually
    fetchPolicy: "cache-and-network",
  });

  // Effect to update polling state based on run statuses
  useEffect(() => {
    if (data?.getSheetAgentRunsByTenderId) {
      const runs = data.getSheetAgentRunsByTenderId;
      setIsPolling(hasActiveRuns(runs));
    }
  }, [data]);

  // Effect to handle polling state changes
  useEffect(() => {
    if (isPolling) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [isPolling, startPolling, stopPolling]);

  return { data, loading, isPolling, startPolling, stopPolling, setIsPolling, refetch };
};
